// ─── Trust Indicators Transformer ────────────────────────────────────────────
//
// Pure function that computes the dashboard-wide trust chip from raw inputs:
//   - how complete is the signal set (X of Y expected)?
//   - how confident is the composite interpretation?
//   - how fresh is the newest data point?
//   - what signals are missing that the user could add?
//
// This module contains zero UI and zero React. It's called by buildDashboardVM.
// ──────────────────────────────────────────────────────────────────────────────

import type { DailyMetrics } from '../../types';
import type { PillarResult } from '../pillarScoring';
import type {
  TrustIndicators,
  ConfidenceLevel,
  CompletenessLevel,
  DataFreshness,
  MissingSignal,
} from './types';

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface TrustInput {
  /** Computed pillar results from pillarScoring.computePillarScores(). */
  pillarResults: Record<string, PillarResult>;
  /** Full metric history (used to find the newest date). */
  metrics: DailyMetrics[];
  /**
   * Clock used to compute freshness. Defaults to Date.now() at call time.
   * Injected for deterministic unit tests.
   */
  now?: Date;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Total signals we expect across all 6 pillars (rough upper bound). */
const EXPECTED_SIGNALS_TOTAL = 24;

/** Minimum distinct scored pillars for "full" completeness. */
const FULL_PILLAR_THRESHOLD = 5;
const PARTIAL_PILLAR_THRESHOLD = 3;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Find the most recent date in a metrics array. Returns null if empty. */
function latestDate(metrics: DailyMetrics[]): Date | null {
  if (metrics.length === 0) return null;
  let newest: string | null = null;
  for (const m of metrics) {
    if (!newest || m.date > newest) newest = m.date;
  }
  if (!newest) return null;
  // Parse as local midday to avoid UTC drift
  return new Date(`${newest}T12:00:00`);
}

/** Human-readable "X ago" label for a date. */
function timeAgoLabel(then: Date, now: Date): string {
  const diffMs = now.getTime() - then.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return 'updated just now';
  if (hours < 24) return `updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'updated 1 day ago';
  if (days < 7) return `updated ${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return 'updated 1 week ago';
  return `updated ${weeks} weeks ago`;
}

/** Absolute date label, e.g. "Today", "Yesterday", "Apr 12", "Dec 31, 2025". */
function absoluteDateLabel(then: Date, now: Date): string {
  const sameYear = then.getFullYear() === now.getFullYear();
  // Compare calendar days (local time)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfThen.getTime()) / (1000 * 60 * 60 * 24));
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  const monthShort = then.toLocaleString('en-US', { month: 'short' });
  if (sameYear) return `${monthShort} ${then.getDate()}`;
  return `${monthShort} ${then.getDate()}, ${then.getFullYear()}`;
}

/** Classify freshness from elapsed days. */
function classifyFreshness(ageDays: number | null): DataFreshness {
  if (ageDays == null) return 'missing';
  if (ageDays < 1) return 'live';
  if (ageDays < 3) return 'recent';
  if (ageDays < 7) return 'stale';
  return 'very_stale';
}

/** Classify completeness from scored pillar count. */
function classifyCompleteness(scoredPillarCount: number): CompletenessLevel {
  if (scoredPillarCount === 0) return 'none';
  if (scoredPillarCount >= FULL_PILLAR_THRESHOLD) return 'full';
  if (scoredPillarCount >= PARTIAL_PILLAR_THRESHOLD) return 'partial';
  return 'sparse';
}

/** Derive confidence from completeness + freshness. */
function deriveConfidence(
  completeness: CompletenessLevel,
  freshness: DataFreshness,
): ConfidenceLevel {
  if (completeness === 'none' || freshness === 'missing') return 'insufficient';
  if (completeness === 'full' && (freshness === 'live' || freshness === 'recent')) return 'high';
  if (completeness === 'full' && freshness === 'stale') return 'moderate';
  if (completeness === 'partial' && (freshness === 'live' || freshness === 'recent')) return 'moderate';
  if (completeness === 'sparse') return 'low';
  return 'low';
}

/** Build the list of pillars with no score as missing signals. */
function findMissingPillars(
  pillarResults: Record<string, PillarResult>,
): MissingSignal[] {
  const out: MissingSignal[] = [];
  const hints: Record<string, string> = {
    sleep: 'Wear your ring or watch overnight',
    movement: 'Connect a step counter',
    nutrition: 'Upload recent labs or complete a weekly check-in',
    stress: 'Enable continuous HRV on your device',
    social: 'Complete the weekly social check-in',
    substance: 'Complete the weekly substance use check-in',
  };
  for (const [key, result] of Object.entries(pillarResults)) {
    if (result.score == null) {
      out.push({
        id: `pillar:${key}`,
        label: result.label || key,
        howToAdd: hints[key] ?? 'Add this data source in Settings',
      });
    }
  }
  return out;
}

// ─── Main transformer ────────────────────────────────────────────────────────

/**
 * Compute the dashboard-wide trust indicators chip. Pure function, safe to
 * call in a useMemo or during server-side rendering.
 */
export function computeTrustIndicators(input: TrustInput): TrustIndicators {
  const { pillarResults, metrics, now = new Date() } = input;

  const scoredPillars = Object.values(pillarResults).filter((p) => p.score != null);
  const scoredCount = scoredPillars.length;

  // Sum available signal inputs across all pillars, capped at the expected
  // total so the trust chip can never read something nonsensical like
  // "28/24 signals" for power users with unusually rich data.
  // `inputCount` defaults to 0 if a pillar result predates the field.
  const rawSignalCount = scoredPillars.reduce(
    (sum, p) => sum + (p.inputCount ?? 0),
    0,
  );
  const signalsExpected = EXPECTED_SIGNALS_TOTAL;
  const signalsAvailable = Math.min(rawSignalCount, signalsExpected);

  const latest = latestDate(metrics);
  const ageDays = latest == null ? null : Math.floor((now.getTime() - latest.getTime()) / (1000 * 60 * 60 * 24));
  const freshness = classifyFreshness(ageDays);
  const completeness = classifyCompleteness(scoredCount);
  const confidence = deriveConfidence(completeness, freshness);

  const lastUpdatedLabel = latest ? timeAgoLabel(latest, now) : 'no recent data';
  const lastUpdatedDateLabel = latest ? absoluteDateLabel(latest, now) : 'No data';
  const missingSignals = findMissingPillars(pillarResults);

  return {
    confidence,
    completeness,
    freshness,
    signalsAvailable,
    signalsExpected,
    lastUpdatedLabel,
    lastUpdatedDateLabel,
    missingSignals,
  };
}
